import { useEffect, useRef, useState } from 'react';
import { Mail, MailCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '@/app/routePaths';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyId } from '@/features/sale-order/hooks/useCompanyId';
import { USER_ROLES } from '@/utils/userRoles';
import { useNow } from '../../hooks/useNow';
import { useMyUnreadMail } from '../api/myUnreadQueries';
import { askNotificationPermission, notificationState, useUnreadNoticePopup, type NotificationState } from '../hooks/useUnreadNoticePopup';
import { badgeText } from '../model/myUnread';
import { MyMailboxList } from './MyMailboxList';

/**
 * The header mail icon for every employee linked to a company mailbox (add-my-unread-mail-notice): the unread
 * total, a list per mailbox, and the server's notices as desktop pop-ups. Hidden for people linked to none.
 */
export function MyUnreadMailBadge() {
  const auth = useAuth();
  const companyId = useCompanyId(auth.companyId);
  const mine = useMyUnreadMail(companyId);
  const now = useNow(60_000);
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState<NotificationState>(() => notificationState());
  const panelRef = useRef<HTMLDivElement | null>(null);

  useUnreadNoticePopup(mine.data?.latestNotice, auth.userName, () => setOpen(true));

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const data = mine.data;
  if (!data || data.mailboxes.length === 0) return null;
  const total = data.total;
  const isSuperAdmin = Number(auth.roleId) === USER_ROLES.SUPERADMIN;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-1.5 transition-all duration-200 hover:bg-slate-100"
        style={{ color: total ? 'var(--color-danger, #e11d48)' : 'var(--color-text-tertiary)' }}
        title={total ? `${total} unread email${total === 1 ? '' : 's'} in your mailboxes` : 'Your mailboxes: all read'}
        aria-label={total ? `${total} unread emails` : 'Your mailboxes: all read'}
      >
        {total ? <Mail className="h-4 w-4" /> : <MailCheck className="h-4 w-4" />}
        {total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
            style={{ backgroundColor: 'var(--color-danger, #e11d48)' }}>
            {badgeText(total)}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl" role="dialog" aria-label="Your unread mail">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Mail className="h-4 w-4 text-blue-600" aria-hidden="true" />
            <span className="text-xs font-black uppercase tracking-wide text-slate-600">Your unread mail</span>
            <span className="ml-auto text-[11px] text-slate-500">{total ? `${total} unread` : 'all read'}</span>
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            <MyMailboxList mailboxes={data.mailboxes} now={now} compact />
          </div>
          <div className="space-y-2 border-t border-slate-100 px-3 py-2 text-[11px] text-slate-500">
            <p>Open your mail program to read and reply. Reminders come after 10 minutes, Mon–Sat 8 am–7 pm.</p>
            {permission === 'default' && (
              <button type="button" onClick={() => void askNotificationPermission().then(setPermission)}
                className="w-full rounded-md bg-blue-600 px-2 py-1.5 text-xs font-bold text-white hover:bg-blue-700">
                Turn on desktop notifications
              </button>
            )}
            {permission === 'denied' && <p>Desktop notifications are blocked for this site in your browser settings.</p>}
            {isSuperAdmin && (
              <Link to={APP_ROUTES.mailMonitor} onClick={() => setOpen(false)} className="block font-semibold text-blue-700 hover:underline">
                Open Mailbox Monitor
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
